/**
 * Procesamiento hooks — TanStack Query wrappers para cola de trabajo y resultados.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "@/lib/query-client";
import {
  capturarResultado,
  corregirResultado,
  getColaTrabajo,
  getContadoresArea,
  getResultado,
  type ColaTrabajoFilters,
  type ColaTrabajoItem,
  type ContadorArea,
  type FlagAnormalidad,
  type MetodoCaptura,
  type AreaLaboratorio,
  type TipoResultado,
  type EstadoOrdenExamen,
  type PrioridadOrden,
  type Resultado,
  type ResultadoCreateData,
  type ResultadoUpdateData,
  type ValorReferenciaItem,
} from "@/services/procesamiento";

// Re-exportar tipos para que las páginas importen desde el hook
export type {
  ColaTrabajoFilters,
  ColaTrabajoItem,
  ContadorArea,
  FlagAnormalidad,
  MetodoCaptura,
  AreaLaboratorio,
  TipoResultado,
  EstadoOrdenExamen,
  PrioridadOrden,
  Resultado,
  ResultadoCreateData,
  ResultadoUpdateData,
  ValorReferenciaItem,
};

// Re-exportar función de servicio para uso directo en páginas (con useCallback)
export { getColaTrabajo };

// =============================================================================
// QUERIES
// =============================================================================

/**
 * Cola de trabajo paginada para una sucursal.
 * Requiere al menos `sucursal` en los filtros.
 */
export function useColaTrabajo(filters: ColaTrabajoFilters) {
  return useQuery({
    queryKey: queryKeys.procesamiento.cola(filters),
    queryFn: () => getColaTrabajo(filters),
    // Solo ejecutar si hay sucursal seleccionada
    enabled: !!filters.sucursal && filters.sucursal > 0,
    // La cola de trabajo cambia frecuentemente — staleTime corto
    staleTime: 30_000, // 30 segundos
  });
}

/**
 * Contadores de exámenes pendientes agrupados por área.
 */
export function useContadoresArea(sucursalId: number | null) {
  return useQuery({
    queryKey: queryKeys.procesamiento.contadores(sucursalId ?? 0),
    queryFn: () => getContadoresArea(sucursalId!),
    enabled: !!sucursalId && sucursalId > 0,
    staleTime: 30_000,
  });
}

/**
 * Resultado activo de un OrdenExamen específico.
 * El id es el ID del OrdenExamen, no del Resultado.
 */
export function useResultado(ordenExamenId: number | null) {
  return useQuery({
    queryKey: queryKeys.procesamiento.resultado(ordenExamenId ?? 0),
    queryFn: () => getResultado(ordenExamenId!),
    enabled: !!ordenExamenId && ordenExamenId > 0,
  });
}

// =============================================================================
// MUTATIONS
// =============================================================================

interface MutationCallbacks {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Capturar resultado de un examen (primera vez).
 * Invalida: cola + contadores + resultado del examen.
 */
export function useCapturarResultado(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ResultadoCreateData) => capturarResultado(data),
    onSuccess: (_resultado, variables) => {
      // Invalidar el resultado específico del examen
      queryClient.invalidateQueries({
        queryKey: queryKeys.procesamiento.resultado(variables.orden_examen_id),
      });
      // Invalidar toda la cola (el examen cambia de estado)
      queryClient.invalidateQueries({
        queryKey: ["procesamiento", "cola"],
      });
      // Invalidar contadores (puede cambiar el conteo por área)
      queryClient.invalidateQueries({
        queryKey: ["procesamiento", "contadores"],
      });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

/**
 * Corregir resultado de un examen ya capturado.
 * Invalida: cola + contadores + resultado del examen.
 */
export function useCorregirResultado(ordenExamenId: number, callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ResultadoUpdateData) => corregirResultado(ordenExamenId, data),
    onSuccess: () => {
      // Invalidar el resultado específico
      queryClient.invalidateQueries({
        queryKey: queryKeys.procesamiento.resultado(ordenExamenId),
      });
      // Invalidar cola y contadores
      queryClient.invalidateQueries({
        queryKey: ["procesamiento", "cola"],
      });
      queryClient.invalidateQueries({
        queryKey: ["procesamiento", "contadores"],
      });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}
